import { useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { HEALTH_CERT_STATES, INSPECTION_SOURCES, type HealthCertApplication, type HygieneInspection, type InspectionSource, type Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

/**
 * Health-certificate application queue —
 * packages/modules/m25-public-health/src/routes/healthCertApplications.ts.
 * Applications originate from the applicant (POST requires their own T2+
 * identity or an X-Acting-For mandate) — this staff portal processes what
 * comes in rather than submitting on anyone's behalf; see build report.
 */
export function Applications() {
  const { persona } = useAuth();
  const isAnyPhaStaff = (persona?.roles.includes('PH_INSPECTOR') || persona?.roles.includes('SENIOR_PHI')) ?? false;
  const isSeniorPhi = persona?.roles.includes('SENIOR_PHI') ?? false;

  const [status, setStatus] = useState('');
  const [district, setDistrict] = useState('');
  const [q, setQ] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  if (district) qs.set('district', district);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<HealthCertApplication>>(`/api/v1/m25/health-cert-applications?${qs.toString()}`);

  return (
    <div>
      <PageHeader
        title="Health Certificate Applications"
        subtitle="Food business, tourist accommodation, salon and pool premises health certificates — 12-month cycle."
      />

      {!isAnyPhaStaff ? (
        <div className="mb-4">
          <ErrorNote message="Listing applications requires PH_INSPECTOR or SENIOR_PHI." />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <input className="field-input !w-52" placeholder="Search premises / ref no…" value={q} onChange={(e) => setQ(e.target.value)} />
        <input className="field-input !w-40" placeholder="District…" value={district} onChange={(e) => setDistrict(e.target.value)} />
        <select className="field-input !w-52" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All states</option>
          {HEALTH_CERT_STATES.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No applications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref no.</th>
                <th className="table-head">Premises</th>
                <th className="table-head">Type</th>
                <th className="table-head">District</th>
                <th className="table-head">State</th>
                <th className="table-head">Grade</th>
                <th className="table-head">Operating</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(a.id)}>
                  <td className="table-cell font-mono text-xs">{a.refNo}</td>
                  <td className="table-cell font-medium">{a.premisesName}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.premisesType} />
                  </td>
                  <td className="table-cell">{a.district}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.state} />
                  </td>
                  <td className="table-cell">{a.currentGrade ? <StatusBadge status={a.currentGrade} /> : '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.operatingStatus} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detailId ? (
        <ApplicationDetailModal id={detailId} isAnyPhaStaff={isAnyPhaStaff} isSeniorPhi={isSeniorPhi} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} />
      ) : null}
    </div>
  );
}

function ApplicationDetailModal({
  id,
  isAnyPhaStaff,
  isSeniorPhi,
  onClose,
  onChanged,
}: {
  id: string;
  isAnyPhaStaff: boolean;
  isSeniorPhi: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detailQ = useApiQuery<HealthCertApplication>(`/api/v1/m25/health-cert-applications/${id}`);
  const inspectionsQ = useApiQuery<{ items: HygieneInspection[] }>(`/api/v1/m25/health-cert-applications/${id}/inspections`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [showSchedule, setShowSchedule] = useState(false);
  const [source, setSource] = useState<InspectionSource>('INITIAL');
  const [scheduledAt, setScheduledAt] = useState('');

  const app = detailQ.data;

  const run = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m25/health-cert-applications/${id}${path}`, body);
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const scheduleInspection = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m25/health-cert-applications/${id}/inspections`, {
        source,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
      });
      setShowSchedule(false);
      detailQ.reload();
      inspectionsQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not schedule inspection');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Health certificate application" onClose={onClose} wide>
      {detailQ.loading || !app ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={app.premisesType} />
            <StatusBadge status={app.state} />
            <StatusBadge status={app.operatingStatus} />
            {app.currentGrade ? <StatusBadge status={app.currentGrade} /> : null}
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Ref no.</dt>
              <dd className="font-mono text-xs">{app.refNo}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Premises</dt>
              <dd className="font-medium">{app.premisesName}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">District</dt>
              <dd className="font-medium">{app.district}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Applicant</dt>
              <dd className="font-medium">
                {app.applicantId} ({app.applicantType})
              </dd>
            </div>
            {app.feeAmountScr != null ? (
              <div className="flex justify-between">
                <dt className="text-slate-500">Fee</dt>
                <dd className="font-medium">{scr(app.feeAmountScr)}</dd>
              </div>
            ) : null}
            {app.certExpiresAt ? (
              <div className="flex justify-between">
                <dt className="text-slate-500">Cert expires</dt>
                <dd className="font-medium">{formatDate(app.certExpiresAt)}</dd>
              </div>
            ) : null}
          </dl>
          {app.rejectionReason ? <p className="text-xs text-rose-600">Rejected: {app.rejectionReason}</p> : null}
          {app.suspendReason ? <p className="text-xs text-amber-600">Suspended: {app.suspendReason}</p> : null}
          {app.revokedReason ? <p className="text-xs text-rose-600">Revoked: {app.revokedReason}</p> : null}

          <div className="border-t border-slate-100 pt-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Inspections</div>
              {isAnyPhaStaff && (app.state === 'SUBMITTED' || app.state === 'INSPECTION_SCHEDULED' || app.state === 'ISSUED') ? (
                <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowSchedule((s) => !s)}>
                  + Schedule inspection
                </button>
              ) : null}
            </div>
            {showSchedule ? (
              <div className="mb-3 flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 p-2.5">
                <Field label="Source" className="!mb-0">
                  <select className="field-input !w-40" value={source} onChange={(e) => setSource(e.target.value as InspectionSource)}>
                    {INSPECTION_SOURCES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Scheduled at (optional)" className="!mb-0">
                  <input type="datetime-local" className="field-input" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
                </Field>
                <button className="btn-primary !px-3 !py-2 text-xs" onClick={scheduleInspection} disabled={busy}>
                  Schedule
                </button>
              </div>
            ) : null}
            {inspectionsQ.loading ? (
              <Spinner />
            ) : (inspectionsQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-xs text-slate-400">No inspections yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {inspectionsQ.data!.items.map((i) => (
                  <li key={i.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-1.5 text-xs">
                    <span>
                      <StatusBadge status={i.source} /> <StatusBadge status={i.status} />
                    </span>
                    <span className="text-slate-500">
                      {i.grade ? <StatusBadge status={i.grade} /> : null} {formatDateTime(i.scheduledAt ?? i.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
            {(app.state === 'SUBMITTED' || app.state === 'INSPECTION_SCHEDULED') && isAnyPhaStaff ? (
              <RejectControl busy={busy} onReject={(r) => run('/reject', { reason: r })} />
            ) : null}
            {app.state === 'FEE_PENDING' ? <p className="text-xs text-slate-400">Awaiting SeyPay payment webhook to issue.</p> : null}
            {app.state === 'ISSUED' && app.operatingStatus === 'ACTIVE' && isSeniorPhi ? (
              <SuspendControl busy={busy} onSuspend={(r) => run('/suspend', { reason: r })} />
            ) : null}
            {app.state === 'ISSUED' && app.operatingStatus === 'SUSPENDED' && isSeniorPhi ? (
              <>
                <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => run('/lift-suspension')} disabled={busy}>
                  Lift suspension
                </button>
                <RevokeControl busy={busy} onRevoke={(r) => run('/revoke', { reason: r })} />
              </>
            ) : null}
            {app.state === 'ISSUED' && app.operatingStatus === 'ACTIVE' && isSeniorPhi ? (
              <RevokeControl busy={busy} onRevoke={(r) => run('/revoke', { reason: r })} />
            ) : null}
            {app.state === 'ISSUED' && app.operatingStatus === 'ACTIVE' && !app.renewedFromId && isAnyPhaStaff ? (
              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => run('/renew')} disabled={busy}>
                Open renewal
              </button>
            ) : null}
          </div>
        </div>
      )}
    </Modal>
  );
}

function RejectControl({ busy, onReject }: { busy: boolean; onReject: (reason: string) => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  if (!open) {
    return (
      <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setOpen(true)}>
        Reject
      </button>
    );
  }
  return (
    <div className="flex gap-1.5">
      <input className="field-input !w-56 !py-1 text-xs" placeholder="Reason…" value={reason} onChange={(e) => setReason(e.target.value)} />
      <button className="btn-danger !px-2.5 !py-1 text-xs" disabled={reason.trim().length < 3 || busy} onClick={() => onReject(reason)}>
        Confirm
      </button>
    </div>
  );
}

function SuspendControl({ busy, onSuspend }: { busy: boolean; onSuspend: (reason: string) => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  if (!open) {
    return (
      <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setOpen(true)}>
        Suspend
      </button>
    );
  }
  return (
    <div className="flex gap-1.5">
      <input className="field-input !w-56 !py-1 text-xs" placeholder="Reason…" value={reason} onChange={(e) => setReason(e.target.value)} />
      <button className="btn-danger !px-2.5 !py-1 text-xs" disabled={reason.trim().length < 3 || busy} onClick={() => onSuspend(reason)}>
        Confirm
      </button>
    </div>
  );
}

function RevokeControl({ busy, onRevoke }: { busy: boolean; onRevoke: (reason: string) => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  if (!open) {
    return (
      <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setOpen(true)}>
        Revoke
      </button>
    );
  }
  return (
    <div className="flex gap-1.5">
      <input className="field-input !w-56 !py-1 text-xs" placeholder="Reason…" value={reason} onChange={(e) => setReason(e.target.value)} />
      <button className="btn-danger !px-2.5 !py-1 text-xs" disabled={reason.trim().length < 3 || busy} onClick={() => onRevoke(reason)}>
        Confirm
      </button>
    </div>
  );
}
